/**
 * Task Import Vuex State
 * Initial state for the task import feature.
 *
 * Key architectural notes:
 * - No batch tracking: Tasks write directly to AppTask table
 * - Simple audit via TaskImportLog (no task linkage)
 * - Mutually exclusive scope: Facility OR FacilityGroup (ForgeBaseTag pattern)
 */

/**
 * Initial state factory function.
 * Returns a fresh state object.
 * @returns {Object} Initial state
 */
export function createInitialState() {
  return {
    // === Wizard State ===
    wizardOpen: false,
    wizardStep: 1, // 1: Config, 2: Field Mapping, 3: Review, 4: Success

    // === Import Mode ===
    importMode: 'existing', // 'existing' | 'new'

    // === Scope Selection (mutually exclusive - ForgeBaseTag pattern) ===
    scopeType: 'Facility', // 'Facility' | 'FacilityGroup'
    selectedFacilityId: null,
    selectedFacilityGroupId: null,

    // === TaskBoard Selection (for existing mode) ===
    selectedTaskTypeId: null,
    selectedTaskType: null, // Full TaskType object with schema
    boardSelectionDisabled: false, // Disable board selector when pre-filled from TaskBoards list

    // === New TaskBoard (for new mode) ===
    newBoardName: '',
    newBoardDescription: '',

    // === Data Source Type ===
    dataSourceType: 'excel', // 'excel' | 'api'

    // === Inline API Configuration (when dataSourceType === 'api') ===
    apiConfig: {
      name: '',
      url: '',
      httpMethod: 'GET',
      requestBody: '',
      headers: [],
      auth: {
        authType: 0,
        apiKeyHeaderName: '',
        apiKeyValue: '',
        bearerToken: '',
        username: '',
        password: '',
        tokenUrl: '',
        clientId: '',
        clientSecret: '',
        scope: '',
      },
      responseDataPath: '',
      pagination: {
        type: 0,
        pageParamName: 'page',
        pageSizeParamName: 'pageSize',
        pageSizeValue: 100,
        offsetParamName: 'offset',
        limitParamName: 'limit',
        limitValue: 100,
        cursorParamName: 'cursor',
        cursorResponsePath: '',
      },
    },
    apiTestResult: null, // { success, statusCode, responseTimeMs, errorMessage }
    apiPreviewData: null, // { headers: [], data: [], totalRecords }

    // === File Upload / Data (populated by either Excel or API preview) ===
    parsedData: [], // Parsed rows (from Excel or API preview)
    excelColumns: [], // Column headers (from Excel or API preview)
    sourceFileName: '',

    // === System Field Mapping ===
    // Maps system field key to Excel column name
    // e.g., { text: 'Task Name', description: 'Notes', status: 'Status' }
    systemFieldMappings: {},

    // === Custom Field Mapping (for existing TaskBoard) ===
    // Maps TaskBoard custom field ID to Excel column name
    // e.g., { 'field-uuid-1': 'Priority Column', 'field-uuid-2': 'Category' }
    customFieldMappings: {},

    // === New Custom Fields (for new TaskBoard) ===
    // Array of new field definitions detected from Excel
    // e.g., [{ excelColumn: 'Priority', displayName: 'Priority', dataType: 6, section: null }]
    newCustomFields: [],

    // === Section Organization (for new TaskBoard) ===
    // e.g., [{ name: 'General', fields: ['text', 'description'] }]
    sections: [],

    // === TaskBoard Sections Configuration (for new TaskBoard - Configure Dialog) ===
    // Full section/field structure matching TaskManager TaskType format
    // [{ id, name, order, fields: [{ id, label, placeHolder, fieldType, color, options, excelColumn }] }]
    taskBoardSections: [],

    // === Key Column for Matching ===
    keyColumn: 'text', // Field used for duplicate matching (Title only per user decision)

    // === Duplicate Check ===
    duplicateCheckResult: null,
    duplicateHandling: 'Overwrite', // 'Overwrite' | 'Skip' - Overwrite is default per user

    // === User Email Resolution (for Users field type) ===
    // Maps email → { userId, displayName, email } or null if unresolved
    userEmailMappings: {},
    // List of emails that couldn't be resolved (for warning display)
    unresolvedUserEmails: [],
    // Loading state for user resolution
    resolvingUsers: false,

    // === Loading States ===
    loading: false,
    importing: false,
    checkingDuplicates: false,
    exporting: false,

    // === Error State ===
    error: null,

    // === Import Result ===
    importResult: null,

    // === Import History Sidebar ===
    historySidebarOpen: false,
    importHistory: [],
    importHistoryTotal: 0,
    historyLoading: false,
    historyFilters: {
      page: 1,
      itemsPerPage: 20,
      status: null, // 'Success' | 'PartialSuccess' | 'Failed' | null (all)
      fromDate: null,
      toDate: null,
    },

    // === Export State ===
    exportResult: null,

    // === Available TaskBoards (cached for selection) ===
    availableTaskTypes: [],
    taskTypesLoading: false,

    // === API Data Sources ===
    apiDataSources: [],
    apiDataSourcesTotal: 0,
    apiDataSourceLoading: false,

    // === API Execution History ===
    apiExecutionHistory: [],
    apiExecutionHistoryTotal: 0,
    apiExecutionHistoryLoading: false,
  };
}

/**
 * Export initial state for Vuex module.
 */
export default createInitialState();
